/**
 * El historial de pagos y devoluciones: qué entró, qué volvió y cuándo.
 *
 * POR QUÉ EXISTE
 * --------------
 * Myriam, 31-oct-2026: "Yo en Xero reviso cuando se cobro si luego se devolvio
 * y si se volvio a cobrar, y eso lo uso para contestar a los clientes". Cuando
 * un cliente llama diciendo que ya pagó, soporte empieza por el cliente
 * (Carlos, 2-nov-2026): por eso el historial junta todas sus facturas.
 *
 * DE DÓNDE SALE
 * -------------
 * De `pagos` de cada factura (ver `pagos_factura.py`): un cobro por entrada, y
 * el que el banco devolvió sigue ahí con `devuelto_en`. Desde el #16 todas las
 * vías apuntan su cobro, y el #17 rellenó las pagadas de antes.
 *
 * Lo que NO está en `pagos` son las devoluciones de antes del #16: el
 * 2-nov-2026 eran las 52 facturas con recibo devuelto, y ninguna tenía su cobro
 * marcado. De ellas solo consta la fecha de la ÚLTIMA (`recibo_devuelto_fecha`)
 * y cuántas fueron (`recibos_devueltos`), así que salen así, sin importe.
 *
 * Y lo que Osagest no sabe todavía: cuando PayGold recupera una devolución no
 * nos avisa hasta la fase 2 (`POST /api/integracion/cobros`). Una factura que
 * sigue en `recibo_devuelto` puede estar ya cobrada allí.
 */
import { dinero, totalCobrado } from './cobros';

/** Por dónde entró el dinero. Lo pone `pagos_factura.nuevo_pago`. */
export const ORIGENES = {
  manual: 'Apuntado a mano',
  remesa: 'Remesa SEPA',
  link: 'Link de pago',
  stripe: 'Stripe',
  impago: 'Cierre del impago',
  // El link de recuperación de una devolución (fase 2, `POST /cobros`).
  paygold: 'Link de PayGold',
  desconocido: 'Origen desconocido',
};

function instante(fecha) {
  const t = Date.parse(fecha || '');
  return Number.isNaN(t) ? null : t;
}

function mismoDia(a, b) {
  return Boolean(a && b) && String(a).slice(0, 10) === String(b).slice(0, 10);
}

/** Los movimientos de una factura, sin ordenar. */
function movimientosDeUna(f) {
  const pagos = f.pagos || [];
  // Un abono no se cobra. Y tampoco se dice «devuelto al cliente»: en esta casa
  // se suele compensar en la siguiente factura (`pagos_factura.estado_al_emitir`),
  // y los 19 que rellenó el #17 no dicen cómo se saldaron.
  const tipoDeEntrada = dinero(f.total) < 0 ? 'abono_saldado' : 'cobro';
  const sigueDevuelta = f.status === 'recibo_devuelto';
  const movimientos = [];

  pagos.forEach((p, i) => {
    const id = p.id || i;
    movimientos.push({
      clave: `${f.id}:${id}:entrada`,
      tipo: tipoDeEntrada,
      fecha: p.fecha || null,
      importe: dinero(p.importe),
      // El origen llegó con el #16. Antes solo `/cobro` escribía en `pagos`
      // (ver `pagos_factura.pendiente_del_documento`): sin origen es a mano.
      origen: p.origen || 'manual',
      metodo: p.metodo || null,
      nota: p.nota || null,
      factura: f,
    });
    if (p.devuelto_en) {
      movimientos.push({
        clave: `${f.id}:${id}:devolucion`,
        tipo: 'devolucion',
        fecha: p.devuelto_en,
        importe: dinero(p.importe),
        motivo: p.devuelto_motivo || null,
        factura: f,
      });
    }
  });

  // Las devoluciones que la factura cuenta y `pagos` no tiene. Son las de antes
  // del #16, pero no solo: si el cobro que volvió no estaba en `pagos`, el
  // servidor no marca nada (`lo_devuelto_esta_apuntado`), también hoy.
  const apuntadas = pagos.filter((p) => p.devuelto_en);
  const contadas = Math.max(Number(f.recibos_devueltos) || 0, f.recibo_devuelto ? 1 : 0);
  const faltan = contadas - apuntadas.length;
  if (faltan > 0) {
    // La fecha guardada es la de la ULTIMA devolución. Si esa ya está en
    // `pagos`, las que faltan son anteriores y su fecha no consta.
    const ultimaYaApuntada = apuntadas.some((p) => mismoDia(p.devuelto_en, f.recibo_devuelto_fecha));
    movimientos.push({
      clave: `${f.id}:sin-cobro-apuntado`,
      tipo: 'devolucion',
      fecha: ultimaYaApuntada ? null : (f.recibo_devuelto_fecha || null),
      importe: null,
      motivo: ultimaYaApuntada ? null : (f.recibo_devuelto_motivo || null),
      sinCobroApuntado: true,
      anteriorALaUltima: ultimaYaApuntada,
      veces: faltan,
      factura: f,
    });
  }

  if (sigueDevuelta) {
    const devoluciones = movimientos.filter((m) => m.tipo === 'devolucion');
    const ultima = devoluciones.sort(masRecienteArriba)[0];
    if (ultima) ultima.recuperacionEnPayGold = true;
  }
  return movimientos;
}

/** Lo más reciente arriba; lo que no tiene fecha, al final. */
function masRecienteArriba(a, b) {
  const ta = instante(a.fecha);
  const tb = instante(b.fecha);
  if (ta === null && tb === null) return 0;
  if (ta === null) return 1;
  if (tb === null) return -1;
  if (ta !== tb) return tb - ta;
  // Mismo instante: la devolución va después del cobro que devuelve, o sea
  // encima en una lista que baja hacia el pasado.
  return (b.tipo === 'devolucion') - (a.tipo === 'devolucion');
}

/**
 * Todos los cobros y devoluciones de estas facturas, lo más reciente arriba.
 *
 * Sin plantillas ni borradores: ni unas ni otros se cobran. Un borrador puede
 * tener número y `pagos`: `convert-to-definitive` copia la proforma entera.
 * Mismo criterio que `resumenDeFacturacion`, o los dos bloques se contradicen.
 */
export function movimientosDeCobro(facturas) {
  return (facturas || [])
    .filter((f) => f && !f.is_recurring && f.invoice_number && f.status !== 'draft')
    .flatMap(movimientosDeUna)
    .sort(masRecienteArriba);
}

/**
 * Facturado, cobrado y pendiente de las facturas de un cliente.
 *
 * Contaba como pendiente solo `accepted` y `overdue`, y como cobrado solo
 * `paid`: una factura con el recibo devuelto o cobrada a medias no salía en
 * ninguno de los dos.
 *
 * Como el servidor (`pagos_factura.pendiente_del_documento`): una pagada no
 * debe nada, y en las demás se resta lo cobrado de `pagos`, que es lo que de
 * verdad entró. Los estados que no se cobran (`returned`, rectificada) solo
 * cuentan en lo facturado, igual que antes.
 */
const SE_COBRAN = ['accepted', 'overdue', 'parcial', 'recibo_devuelto'];

export function resumenDeFacturacion(facturas) {
  return (facturas || []).reduce(
    (acc, f) => {
      if (f.is_recurring || f.status === 'draft') return acc;
      const total = dinero(f.total);
      acc.facturado = dinero(acc.facturado + total);
      if (f.status === 'paid') {
        acc.cobrado = dinero(acc.cobrado + total);
      } else if (SE_COBRAN.includes(f.status)) {
        // Con el signo del documento: en un abono lo devuelto también resta.
        const cobrado = Math.sign(total) * Math.min(totalCobrado(f.pagos), Math.abs(total));
        acc.cobrado = dinero(acc.cobrado + cobrado);
        acc.pendiente = dinero(acc.pendiente + total - cobrado);
      }
      return acc;
    },
    { facturado: 0, cobrado: 0, pendiente: 0 }
  );
}
